import { createSignal, createResource, createMemo, createEffect, For, Index, Show } from 'solid-js';
import SchemeDocEditor from '../components/SchemeDocEditor.jsx';
import DslHelp from '../components/DslHelp.jsx';

import { formatMoney, formatSigned } from '../lib/money.js';
import { activeCompany, me } from '../lib/session.js';

const WEEKDAYS = ['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu'];

// The MAIN company (PT. Besi Baja Metalindo); its schemes are the templates and
// can't be deleted. Mirrors store.mainCompanyID.
const MAIN_COMPANY = 1;

// Komponen Gaji — the whole scheme as ONE document. The outline sidebar is
// derived from the live compile; the editor saves immutable, effective-dated
// versions of the complete document (a pay run pins exactly one version).

const KIND_LABEL = { earning: 'Pendapatan', deduction: 'Potongan', info: 'Info', calc: 'Calc' };
const KIND_DOT = { earning: 'earning', deduction: 'deduction', info: 'info', calc: 'calc' };

async function getJSON(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error('HTTP ' + res.status);
  return res.json();
}

async function postJSON(url, body) {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, status: res.status, data };
}

// Local calendar date — toISOString() would give the UTC date, which is
// yesterday for the first 7 hours of every Indonesian (UTC+7) workday.
const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// Hand-rolled LCS line diff for the version history view.
function diffLines(a, b) {
  const A = a.split('\n'), B = b.split('\n');
  const n = A.length, m = B.length;
  const dp = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--)
    for (let j = m - 1; j >= 0; j--)
      dp[i][j] = A[i] === B[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
  const out = [];
  let i = 0, j = 0;
  while (i < n && j < m) {
    if (A[i] === B[j]) { out.push({ t: ' ', s: A[i] }); i++; j++; }
    else if (dp[i + 1][j] >= dp[i][j + 1]) { out.push({ t: '-', s: A[i] }); i++; }
    else { out.push({ t: '+', s: B[j] }); j++; }
  }
  while (i < n) out.push({ t: '-', s: A[i++] });
  while (j < m) out.push({ t: '+', s: B[j++] });
  // Collapse long unchanged runs to keep the view focused on the change.
  const res = [];
  let run = [];
  const flush = () => {
    if (run.length > 5) {
      res.push(run[0], run[1], { t: '…', s: `… ${run.length - 4} baris sama …` }, run[run.length - 2], run[run.length - 1]);
    } else res.push(...run);
    run = [];
  };
  for (const line of out) {
    if (line.t === ' ') run.push(line);
    else { flush(); res.push(line); }
  }
  flush();
  return res;
}

export default function KomponenGaji() {
  const [schemes, { refetch: refetchSchemes }] = createResource(() => getJSON('/api/schemes'));
  const [builtins] = createResource(() => getJSON('/api/formula/builtins'));
  const [showHelp, setShowHelp] = createSignal(false);
  const [outlineOpen, setOutlineOpen] = createSignal(true); // collapse the komponen rail

  const [schemeCode, setSchemeCode] = createSignal('');
  createEffect(() => {
    const list = schemes();
    if (list?.length && !schemeCode()) setSchemeCode(list[0].code);
  });

  const [doc, { refetch: refetchDoc }] = createResource(
    schemeCode,
    (code) => code ? getJSON(`/api/scheme/${code}/doc`) : null,
  );

  // ── Editor state ───────────────────────────────────────────────────────
  const [source, setSource] = createSignal('');
  const [loadedSource, setLoadedSource] = createSignal('');
  const [check, setCheck] = createSignal(null); // /api/scheme/doc/check result
  const [effFrom, setEffFrom] = createSignal(today());
  const [note, setNote] = createSignal('');
  const [verdict, setVerdict] = createSignal(null);
  const [busy, setBusy] = createSignal(false);
  const [showHistory, setShowHistory] = createSignal(false);
  let editorApi;

  createEffect(() => {
    const d = doc();
    if (d) {
      setSource(d.source);
      setLoadedSource(d.source);
      setEffFrom(today());
      // verdict deliberately NOT reset here: a refetch after save must keep
      // the "tersimpan sebagai versi N" message visible.
    }
  });

  const dirty = createMemo(() => source() !== loadedSource());
  const components = createMemo(() => check()?.components ?? doc()?.components ?? []);
  const counts = createMemo(() => {
    const c = { earning: 0, deduction: 0, info: 0, calc: 0, input: 0 };
    for (const comp of components()) {
      c[comp.kind] = (c[comp.kind] ?? 0) + 1;
      if (comp.mode === 'input') c.input++;
    }
    return c;
  });

  const [history, { refetch: refetchHistory }] = createResource(
    () => showHistory() ? schemeCode() : null,
    (code) => getJSON(`/api/scheme/${code}/doc/history`),
  );

  // ── Tipe Penggajian editor (per-scheme payroll types) ─────────────────
  const [showTipes, setShowTipes] = createSignal(false);
  const [tipes, { refetch: refetchTipes }] = createResource(
    () => showTipes() ? schemeCode() : null,
    (code) => getJSON(`/api/scheme/${code}/tipes`),
  );
  const [tipeRows, setTipeRows] = createSignal([]);
  createEffect(() => { const l = tipes(); if (l) setTipeRows(l.map((t) => ({ ...t }))); });
  const addTipeRow = () => setTipeRows([...tipeRows(), { code: '', label: '', needsRincian: false, periodType: '', periodStartDay: 21, periodStartWeekday: 1 }]);
  const setTipeField = (i, k, v) => { const r = tipeRows().slice(); r[i] = { ...r[i], [k]: v }; setTipeRows(r); };
  const removeTipeRow = (i) => { const r = tipeRows().slice(); r.splice(i, 1); setTipeRows(r); };
  async function saveTipes() {
    // Coerce the period fields (inputs hold strings; weekday 0 = Minggu is valid).
    const rows = tipeRows().map((t) => ({
      ...t,
      periodType: t.periodType || '',
      periodStartDay: Number(t.periodStartDay) || 21,
      periodStartWeekday: Number.isNaN(Number(t.periodStartWeekday)) ? 1 : Number(t.periodStartWeekday),
    }));
    const r = await postJSON(`/api/scheme/${schemeCode()}/tipes`, { tipes: rows });
    if (r.ok) { setVerdict({ ok: true, msg: 'Tipe penggajian tersimpan.' }); refetchTipes(); }
    else setVerdict({ ok: false, msg: r.data.error || `Gagal menyimpan tipe (HTTP ${r.status}).` });
  }

  // ── Kolom Rincian Harian editor (per-scheme per-day columns, §4q; super) ──
  const [showDayCols, setShowDayCols] = createSignal(false);
  const [dayColsRes, { refetch: refetchDayCols }] = createResource(
    () => showDayCols() ? schemeCode() : null,
    (code) => getJSON(`/api/scheme/${code}/day-columns`),
  );
  const [dayColRows, setDayColRows] = createSignal([]);
  createEffect(() => {
    const l = dayColsRes();
    if (l) setDayColRows(l.map((c) => ({
      name: c.name, label: c.label, valueType: c.valueType,
      choicesText: (c.choices || []).join(', '), defaultVal: c.defaultVal || '',
    })));
  });
  const addDayColRow = () => setDayColRows([...dayColRows(), { name: '', label: '', valueType: 'enum', choicesText: '', defaultVal: '' }]);
  const setDayColField = (i, k, v) => { const r = dayColRows().slice(); r[i] = { ...r[i], [k]: v }; setDayColRows(r); };
  const removeDayColRow = (i) => { const r = dayColRows().slice(); r.splice(i, 1); setDayColRows(r); };
  async function saveDayCols() {
    const columns = dayColRows().map((c) => ({
      name: c.name.trim(), label: c.label.trim(), valueType: c.valueType,
      choices: c.choicesText.split(',').map((s) => s.trim()).filter(Boolean),
      defaultVal: c.defaultVal.trim(),
    }));
    const r = await postJSON(`/api/scheme/${schemeCode()}/day-columns`, { columns });
    if (r.ok) { setVerdict({ ok: true, msg: 'Kolom rincian harian tersimpan.' }); refetchDayCols(); }
    else setVerdict({ ok: false, msg: r.data.error || `Gagal menyimpan kolom (HTTP ${r.status}).` });
  }

  // ── Dry run (Uji Coba): draft document vs a historical run ────────────
  const [showDry, setShowDry] = createSignal(false);
  const [runs] = createResource(
    () => showDry() ? schemeCode() : null,
    (code) => getJSON(`/api/runs?scheme=${code}`),
  );
  const [dryRunId, setDryRunId] = createSignal(0);
  const [dry, setDry] = createSignal(null);      // dry-run response
  const [dryBusy, setDryBusy] = createSignal(false);
  const [dryAll, setDryAll] = createSignal(false); // show unchanged components too
  createEffect(() => {
    const list = runs();
    if (list?.length && !dryRunId()) setDryRunId(list[0].id);
  });
  createEffect(() => { schemeCode(); setDry(null); setDryRunId(0); });

  async function runDry() {
    setDryBusy(true);
    setDry(null);
    const r = await postJSON(`/api/scheme/${schemeCode()}/dryrun`, {
      source: source(), runId: dryRunId(),
    });
    setDryBusy(false);
    setDry(r.ok ? r.data : { failed: r.data.error ?? `HTTP ${r.status}` });
  }

  async function save() {
    setBusy(true);
    setVerdict(null);
    const r = await postJSON(`/api/scheme/${schemeCode()}/doc`, {
      source: source(), effectiveFrom: effFrom(), note: note(),
    });
    setBusy(false);
    if (r.ok) {
      setVerdict({ ok: true, msg: `Tersimpan sebagai versi ${r.data.version}, berlaku sejak ${effFrom()}.` });
      setLoadedSource(source());
      setNote('');
      refetchDoc();
      if (showHistory()) refetchHistory();
    } else {
      setVerdict({ ok: false, msg: r.data.error ?? `Gagal menyimpan (HTTP ${r.status}).` });
    }
  }

  // saveInPlace overwrites the CURRENT version's document without bumping the
  // version (superadmin tidy-up). Versions are normally immutable, so this asks
  // for confirmation and is only offered to superadmins.
  async function saveInPlace() {
    const v = doc()?.version;
    if (!v) return;
    if (!confirm(`Timpa dokumen versi ${v} dengan isi editor sekarang?\n\nNomor versi TIDAK bertambah — perubahan langsung menggantikan dokumen versi ini (untuk merapikan komentar/format).`)) return;
    setBusy(true);
    setVerdict(null);
    const r = await postJSON(`/api/scheme/${schemeCode()}/doc/replace`, { source: source(), version: v });
    setBusy(false);
    if (r.ok) {
      setVerdict({ ok: true, msg: `Versi ${r.data.version} disimpan langsung (tanpa versi baru).` });
      setLoadedSource(source());
      refetchDoc();
      if (showHistory()) refetchHistory();
    } else {
      setVerdict({ ok: false, msg: r.data.error ?? `Gagal menyimpan (HTTP ${r.status}).` });
    }
  }

  // ── Create scheme (clone) ──────────────────────────────────────────────
  const [creating, setCreating] = createSignal(false);
  const [sources] = createResource(creating, (c) => (c ? getJSON('/api/schemes/all') : null));
  const [nc, setNc] = createSignal({ code: '', name: '', source: '' });
  const ncSet = (k, v) => setNc({ ...nc(), [k]: v });
  const [createMsg, setCreateMsg] = createSignal(null);
  const [busyCreate, setBusyCreate] = createSignal(false);
  async function submitCreate() {
    setBusyCreate(true);
    setCreateMsg(null);
    const r = await postJSON('/api/schemes', nc());
    setBusyCreate(false);
    if (r.ok) {
      const code = nc().code.trim();
      setNc({ code: '', name: '', source: '' });
      setCreating(false);
      await refetchSchemes();
      setSchemeCode(code);
    } else setCreateMsg({ ok: false, t: r.data.error || `HTTP ${r.status}` });
  }

  // Delete a scheme — only offered for non-main companies (the backend enforces).
  const canDelete = () => activeCompany() !== MAIN_COMPANY;
  async function deleteScheme() {
    const code = schemeCode();
    if (!code) return;
    if (!confirm(`Hapus skema "${code}"? Dokumen, variabel, & aturan slip-nya ikut terhapus. Tidak bisa dibatalkan.`)) return;
    const res = await fetch(`/api/scheme/${encodeURIComponent(code)}`, { method: 'DELETE' });
    if (res.ok) {
      setSchemeCode('');
      await refetchSchemes();
    } else {
      alert((await res.json().catch(() => ({}))).error || 'gagal menghapus skema');
    }
  }

  return (
    <>
      <div class="page-head">
        <h2>Komponen Gaji</h2>
        <Show when={doc()}>
          <span class="kg-meta-text">
            v{doc().version} · berlaku sejak {doc().effectiveFrom}
            <Show when={dirty()}> · <b>diubah</b></Show>
          </span>
        </Show>
        <div class="spacer" />
        <Show when={check()}>
          <span class={`badge ${check().valid ? 'success' : 'danger'}`}>
            {check().valid ? 'skema valid' : `${check().errors.length} error`}
          </span>
        </Show>
        <Show when={schemes()}>
          <div class="seg">
            <For each={schemes()}>{(s) =>
              <button
                class="seg-item"
                classList={{ active: schemeCode() === s.code }}
                onClick={() => {
                  if (dirty() && !confirm('Perubahan belum disimpan akan hilang. Lanjut?')) return;
                  setVerdict(null);
                  setNote('');
                  setSchemeCode(s.code);
                }}
              >
                {s.code}
              </button>
            }</For>
          </div>
        </Show>
        <button class="btn ghost" onClick={() => { setCreating(!creating()); setCreateMsg(null); }}>
          {creating() ? 'Tutup' : '+ Skema'}
        </button>
        <Show when={schemeCode() && canDelete()}>
          <button class="btn ghost kg-del" onClick={deleteScheme} title="Hapus skema ini">Hapus skema</button>
        </Show>
      </div>

      {/* Create a scheme for the active company by cloning an existing one. */}
      <Show when={creating()}>
        <div class="card card-pad kg-create">
          <h4 class="kg-hist-title">Buat skema baru (untuk perusahaan aktif)</h4>
          <p class="ky-vars-note">
            Skema baru <b>disalin</b> dari skema lain — komponen, periode, & variabel default ikut
            tersalin, lalu bisa Anda ubah. Kode skema harus <b>unik antar perusahaan</b> (mis.
            <code> OFFICE-PBM</code>), karena kode bersifat global.
          </p>
          <div class="kg-create-grid">
            <label class="kg-field"><span>Kode skema</span>
              <input type="text" placeholder="mis. OFFICE-PBM" value={nc().code}
                onInput={(e) => ncSet('code', e.currentTarget.value)} /></label>
            <label class="kg-field"><span>Nama</span>
              <input type="text" placeholder="mis. Office Pasir Baja Metalindo" value={nc().name}
                onInput={(e) => ncSet('name', e.currentTarget.value)} /></label>
            <label class="kg-field"><span>Salin dari</span>
              <select class="kg-select" value={nc().source} onInput={(e) => ncSet('source', e.currentTarget.value)}>
                <option value="">— pilih skema sumber —</option>
                <For each={sources() ?? []}>{(s) =>
                  <option value={s.code}>{s.code} · {s.name} ({s.company})</option>
                }</For>
              </select></label>
          </div>
          <Show when={createMsg()}>
            <div class={`kg-verdict ${createMsg().ok ? 'ok' : 'fail'}`}>{createMsg().t}</div>
          </Show>
          <div class="kg-create-actions">
            <button class="btn primary" disabled={busyCreate()} onClick={submitCreate}>
              {busyCreate() ? 'Membuat…' : 'Buat skema'}
            </button>
            <button class="btn ghost" onClick={() => setCreating(false)}>Batal</button>
          </div>
        </div>
      </Show>

      <Show when={schemeCode()} fallback={
        <Show when={schemes() && schemes().length === 0 && !creating()}>
          <div class="card card-pad kg-empty">
            <p class="kg-meta-text">
              Belum ada skema untuk perusahaan ini. Klik <b>+ Skema</b> di atas untuk membuat
              (salin dari skema perusahaan lain), lalu skema akan muncul untuk dipilih saat
              membuat karyawan.
            </p>
          </div>
        </Show>
      }>
      <div class="kg-layout" classList={{ 'outline-collapsed': !outlineOpen() }}>
        {/* ── Outline (live, from the compiler) ───────────────────────── */}
        <div class="card kg-outline">
          <div class="kg-outline-head">
            <button class="kg-outline-toggle" title={outlineOpen() ? 'Sembunyikan daftar komponen' : 'Tampilkan daftar komponen'}
              onClick={() => setOutlineOpen((o) => !o)}>{outlineOpen() ? '⟨' : '⟩'}</button>
            <Show when={outlineOpen()}>
              <span>{components().length} komponen</span>
              <span class="kg-outline-counts">
                {counts().earning}↑ {counts().deduction}↓ {counts().calc} calc {counts().input} input
              </span>
            </Show>
          </div>
          <div class="kg-outline-list">
            <For each={components()}>{(c) =>
              <button class="kg-outline-item" onClick={() => editorApi?.scrollToLine(c.line)}>
                <span class={`kdot ${KIND_DOT[c.kind] ?? 'info'}`} title={KIND_LABEL[c.kind] ?? c.kind} />
                <span class="kg-outline-code">{c.code}</span>
                <span class="kg-outline-mode">{c.mode === 'input' ? c.dataType : ''}</span>
              </button>
            }</For>
          </div>
        </div>

        {/* ── Document editor ─────────────────────────────────────────── */}
        <div class="kg-editor">
          <SchemeDocEditor
            source={source}
            setSource={setSource}
            builtins={() => builtins() ?? []}
            onCheck={setCheck}
            ready={(api) => { editorApi = api; }}
          />

          <div class="kg-save-row">
            <label class="kg-field">
              <span>Berlaku sejak</span>
              <input type="date" value={effFrom()} onInput={(e) => setEffFrom(e.currentTarget.value)} />
            </label>
            <label class="kg-field kg-field-grow">
              <span>Catatan perubahan</span>
              <input
                type="text"
                placeholder="mis. SK Direksi 12/2026 — pembagi hari kerja jadi 25"
                value={note()}
                onInput={(e) => setNote(e.currentTarget.value)}
              />
            </label>
            <button class="btn" onClick={() => setShowTipes(!showTipes())}>
              {showTipes() ? 'Tutup Tipe' : 'Tipe Penggajian'}
            </button>
            <Show when={me()?.role === 'superadmin'}>
              <button class="btn" onClick={() => setShowDayCols(!showDayCols())}>
                {showDayCols() ? 'Tutup Kolom Rincian' : 'Kolom Rincian'}
              </button>
            </Show>
            <button class="btn" onClick={() => setShowDry(!showDry())}>
              {showDry() ? 'Tutup Uji Coba' : 'Uji Coba'}
            </button>
            <button class="btn" onClick={() => setShowHistory(!showHistory())}>
              {showHistory() ? 'Tutup Riwayat' : 'Riwayat'}
            </button>
            <button class="btn" title="Bantuan: sintaks & fungsi DSL (jendela mengambang)"
              onClick={() => setShowHelp(!showHelp())}>
              ? Sintaks
            </button>
            <Show when={me()?.role === 'superadmin'}>
              <button
                class="btn"
                disabled={busy() || !dirty() || !(check()?.valid)}
                onClick={saveInPlace}
                title="Timpa dokumen versi ini tanpa membuat versi baru (untuk merapikan)"
              >
                Simpan Langsung
              </button>
            </Show>
            <button
              class="btn primary"
              disabled={busy() || !dirty() || !(check()?.valid)}
              onClick={save}
              title={dirty() ? 'Simpan seluruh dokumen sebagai versi baru (versi lama tidak diubah)' : 'Belum ada perubahan'}
            >
              Simpan Versi Baru
            </button>
          </div>

          <Show when={verdict()}>
            <div class={`kg-verdict ${verdict().ok ? 'ok' : 'fail'}`}>{verdict().msg}</div>
          </Show>

          {/* ── Tipe Penggajian: per-scheme payroll types (scope tags) ───── */}
          <Show when={showTipes()}>
            <div class="card card-pad kg-tipes">
              <h4 class="kg-hist-title">Tipe Penggajian — {schemeCode()}</h4>
              <p class="ky-vars-note">
                Tipe penggajian mem-<i>scope</i> komponen lewat tag <code>@KodeTipe</code> di awal baris
                (mis. <code>@Komisi calc Omzet = …</code>). Saat membuat pemrosesan gaji, admin dapat
                menjalankan skema untuk satu tipe — hanya komponen bertag tipe itu <b>plus</b> komponen
                tanpa tag yang dihitung & tampil di input per periode. Centang <b>Rincian Harian</b> bila
                tipe ini perlu data kehadiran &amp; lembur; bila tidak dicentang, grid Rincian Harian tidak
                ditampilkan dan stored procedure derivasi tidak dipanggil saat pemrosesan gaji. Kode dipakai
                sebagai tag: huruf/angka/garis bawah, tanpa spasi. <b>Periode</b>: tiap tipe boleh memakai
                periode sendiri (mis. komisi mulai tanggal 1, gaji mulai tanggal 21) — kosongkan (<i>Ikuti
                skema</i>) untuk memakai periode skema; berlaku untuk pemrosesan gaji BARU saja.
              </p>
              <table class="data">
                <thead>
                  <tr><th>Kode (tag)</th><th>Label</th><th class="num">Rincian Harian?</th><th>Periode</th><th /></tr>
                </thead>
                <tbody>
                  <Index each={tipeRows()}>{(t, i) =>
                    <tr>
                      <td><input class="pg-cell" value={t().code} placeholder="Komisi"
                        onInput={(e) => setTipeField(i, 'code', e.currentTarget.value)} /></td>
                      <td><input class="pg-cell" value={t().label} placeholder="Gaji Komisi"
                        onInput={(e) => setTipeField(i, 'label', e.currentTarget.value)} /></td>
                      <td class="num"><input type="checkbox" checked={t().needsRincian}
                        onInput={(e) => setTipeField(i, 'needsRincian', e.currentTarget.checked)} /></td>
                      <td>
                        <div class="kg-tipe-period">
                          <select class="kg-select" value={t().periodType || ''}
                            onInput={(e) => setTipeField(i, 'periodType', e.currentTarget.value)}>
                            <option value="">Ikuti skema</option>
                            <option value="Bulanan">Bulanan</option>
                            <option value="Mingguan">Mingguan</option>
                          </select>
                          <Show when={t().periodType === 'Bulanan'}>
                            <input class="pg-cell kg-tipe-day" type="number" min="1" max="28"
                              title="tanggal mulai periode (1–28)"
                              value={t().periodStartDay ?? 21}
                              onInput={(e) => setTipeField(i, 'periodStartDay', e.currentTarget.value)} />
                          </Show>
                          <Show when={t().periodType === 'Mingguan'}>
                            <select class="kg-select" title="hari mulai periode"
                              value={String(t().periodStartWeekday ?? 1)}
                              onInput={(e) => setTipeField(i, 'periodStartWeekday', e.currentTarget.value)}>
                              <For each={WEEKDAYS}>{(w, wi) => <option value={String(wi())}>{w}</option>}</For>
                            </select>
                          </Show>
                        </div>
                      </td>
                      <td><button class="btn ghost" title="Hapus tipe" onClick={() => removeTipeRow(i)}>✕</button></td>
                    </tr>
                  }</Index>
                  <Show when={tipeRows().length === 0}>
                    <tr><td colspan="5" class="kg-meta-text">Belum ada tipe — skema berjalan tanpa scoping (semua komponen).</td></tr>
                  </Show>
                </tbody>
              </table>
              <div class="kg-create-actions">
                <button class="btn ghost" onClick={addTipeRow}>+ Tipe</button>
                <div class="spacer" />
                <button class="btn primary" onClick={saveTipes}>Simpan Tipe</button>
              </div>
            </div>
          </Show>

          {/* ── Kolom Rincian Harian: per-day operational columns (superadmin) ─ */}
          <Show when={showDayCols()}>
            <div class="card card-pad kg-tipes">
              <h4 class="kg-hist-title">Kolom Rincian Harian — {schemeCode()}</h4>
              <p class="ky-vars-note">
                Kolom operasional yang diisi <b>per hari per karyawan</b> di grid Rincian Harian (mis. TKBM:{' '}
                <code>Mode</code> Kirim/Gudang, <code>Kendaraan</code> Besar/Kecil) dan dibaca oleh stored
                procedure derivasi. Hanya skema yang butuh fakta harian non-absensi yang perlu kolom ini —
                skema lain dibiarkan kosong (grid tak muncul). Tipe <b>enum</b> = dropdown (pilihan dipisah
                koma). Nama: huruf/angka/garis bawah tanpa spasi (proc membacanya persis).
              </p>
              <table class="data">
                <thead>
                  <tr><th>Nama</th><th>Label</th><th>Tipe</th><th>Pilihan (enum)</th><th>Default</th><th /></tr>
                </thead>
                <tbody>
                  <Index each={dayColRows()}>{(c, i) =>
                    <tr>
                      <td><input class="pg-cell" value={c().name} placeholder="Mode"
                        onInput={(e) => setDayColField(i, 'name', e.currentTarget.value)} /></td>
                      <td><input class="pg-cell" value={c().label} placeholder="Mode Kerja"
                        onInput={(e) => setDayColField(i, 'label', e.currentTarget.value)} /></td>
                      <td><select class="kg-select" value={c().valueType}
                        onInput={(e) => setDayColField(i, 'valueType', e.currentTarget.value)}>
                        <option value="enum">enum</option><option value="teks">teks</option><option value="angka">angka</option>
                      </select></td>
                      <td><input class="pg-cell" value={c().choicesText} placeholder="Kirim, Gudang"
                        disabled={c().valueType !== 'enum'}
                        onInput={(e) => setDayColField(i, 'choicesText', e.currentTarget.value)} /></td>
                      <td><input class="pg-cell" value={c().defaultVal} placeholder="Gudang"
                        onInput={(e) => setDayColField(i, 'defaultVal', e.currentTarget.value)} /></td>
                      <td><button class="btn ghost" title="Hapus kolom" onClick={() => removeDayColRow(i)}>✕</button></td>
                    </tr>
                  }</Index>
                  <Show when={dayColRows().length === 0}>
                    <tr><td colspan="6" class="kg-meta-text">Belum ada kolom — skema ini tidak menampilkan Rincian Harian.</td></tr>
                  </Show>
                </tbody>
              </table>
              <div class="kg-create-actions">
                <button class="btn ghost" onClick={addDayColRow}>+ Kolom</button>
                <div class="spacer" />
                <button class="btn primary" onClick={saveDayCols}>Simpan Kolom</button>
              </div>
            </div>
          </Show>

          {/* ── Uji Coba: evaluate the DRAFT against a real historical run ── */}
          <Show when={showDry()}>
            <div class="card card-pad kg-dry">
              <div class="kg-dry-bar">
                <h4 class="kg-hist-title">Uji Coba — dokumen di editor vs pemrosesan gaji historis</h4>
                <div class="spacer" />
                <select
                  class="kg-select"
                  value={dryRunId()}
                  onInput={(e) => setDryRunId(Number(e.currentTarget.value))}
                >
                  <For each={runs() ?? []}>{(r) =>
                    <option value={r.id}>
                      {r.periodStart} — {r.legacyNo || ('pemrosesan ' + r.id)} ({r.persons} karyawan)
                    </option>
                  }</For>
                </select>
                <button
                  class="btn primary"
                  disabled={dryBusy() || !dryRunId() || !(check()?.valid)}
                  onClick={runDry}
                >
                  {dryBusy() ? 'Menghitung…' : 'Jalankan'}
                </button>
              </div>

              <Show when={dry()?.failed}>
                <div class="kg-verdict fail">{dry().failed}</div>
              </Show>

              <Show when={dry() && !dry().failed}>
                <div class={`kg-dry-summary ${dry().changedPersons ? 'warn' : 'ok'}`}>
                  <Show
                    when={dry().changedPersons > 0}
                    fallback={<>Tidak ada perubahan — dokumen ini menghasilkan nilai yang identik untuk {dry().persons} karyawan.</>}
                  >
                    <b>{dry().changedPersons}</b> dari {dry().persons} karyawan berubah.
                  </Show>
                  <Show when={dry().errors?.length > 0}>
                    {' '}<span class="kg-dry-err">{dry().errors.length} error evaluasi.</span>
                  </Show>
                </div>

                <Show when={dry().components.some(c => c.changed > 0) || dryAll()}>
                  <table class="data kg-dry-table">
                    <thead>
                      <tr>
                        <th>Komponen</th>
                        <th class="num">Total lama</th>
                        <th class="num">Total baru</th>
                        <th class="num">Δ</th>
                        <th class="num">Karyawan</th>
                      </tr>
                    </thead>
                    <tbody>
                      <For each={dry().components.filter(c => dryAll() || c.changed > 0)}>{(c) =>
                        <tr>
                          <td><span class={`kdot ${KIND_DOT[c.kind] ?? 'info'}`} /> <code>{c.code}</code></td>
                          <td class="num money">{formatMoney(c.oldTotal)}</td>
                          <td class="num money">{formatMoney(c.newTotal)}</td>
                          {/* sign carries no good/bad color: a bigger number
                              can be a bigger DEDUCTION — direction ≠ verdict */}
                          <td class="num money kg-delta">{formatSigned(c.delta)}</td>
                          <td class="num">{c.changed || ''}</td>
                        </tr>
                      }</For>
                    </tbody>
                  </table>
                  <button class="btn ghost kg-dry-toggle" onClick={() => setDryAll(!dryAll())}>
                    {dryAll() ? 'hanya yang berubah' : 'tampilkan semua komponen'}
                  </button>
                </Show>

                <Show when={dry().employees?.length > 0}>
                  <h4 class="kg-hist-title">Per karyawan</h4>
                  <For each={dry().employees}>{(emp) =>
                    <details class="kg-hist-entry">
                      <summary><b>{emp.nik}</b> {emp.name} — {emp.changes.length} komponen berubah</summary>
                      <table class="data kg-dry-table">
                        <tbody>
                          <For each={emp.changes}>{(ch) =>
                            <tr>
                              <td><code>{ch.code}</code></td>
                              <td class="num money">{formatMoney(ch.old)}</td>
                              <td>→</td>
                              <td class="num money">{formatMoney(ch.new)}</td>
                            </tr>
                          }</For>
                        </tbody>
                      </table>
                    </details>
                  }</For>
                </Show>

                <Show when={dry().errors?.length > 0}>
                  <h4 class="kg-hist-title">Error evaluasi</h4>
                  <For each={dry().errors}>{(e) =>
                    <div class="ferr">⚠ {e.nik} · {e.code}: {e.msg}</div>
                  }</For>
                </Show>
              </Show>
            </div>
          </Show>

          {/* ── Version history: each entry diffs against its predecessor ── */}
          <Show when={showHistory() && history()}>
            <div class="card card-pad kg-history">
              <h4 class="kg-hist-title">Riwayat versi — {schemeCode()}</h4>
              <For each={history()}>{(h, idx) => {
                const prev = () => history()[idx() + 1];
                return (
                  <details class="kg-hist-entry">
                    <summary>
                      <b>v{h.version}</b> · berlaku {h.effectiveFrom} · {h.createdAt} · {h.createdBy}
                      <Show when={h.note}> — <i>{h.note}</i></Show>
                      <button
                        class="btn ghost kg-hist-load"
                        onClick={(e) => {
                          e.preventDefault();
                          if (dirty() && !confirm('Perubahan belum disimpan akan hilang. Muat versi ini?')) return;
                          setSource(h.source);
                          setVerdict(null);
                        }}
                      >
                        muat ke editor
                      </button>
                    </summary>
                    <pre class="kg-diff"><For each={prev() ? diffLines(prev().source, h.source) : h.source.split('\n').map(s => ({ t: ' ', s }))}>{(l) =>
                      <div class={l.t === '+' ? 'dadd' : l.t === '-' ? 'ddel' : l.t === '…' ? 'dskip' : ''}>{l.t} {l.s}</div>
                    }</For></pre>
                  </details>
                );
              }}</For>
            </div>
          </Show>
        </div>
      </div>
      </Show>

      <Show when={showHelp()}>
        <DslHelp builtins={() => builtins() ?? []} onClose={() => setShowHelp(false)} />
      </Show>
    </>
  );
}
